import { useState } from "react";
import { COLORS } from "../shared/constants";

/**
 * AmendmentModal — modal for amending a session or assessment field.
 *
 * Props:
 *   type: "session" | "assessment"
 *   record: the session or assessment object being amended
 *   field: which field is being amended (e.g. "motor_threshold_value", "total_score")
 *   fieldLabel: human-readable label for the field
 *   currentValue: the current value of the field
 *   onSubmit: async (amendmentData) => void — called with { field_changed, new_value, reason }
 *   onClose: () => void
 *   users: array of user objects (for displaying amended_by names)
 */
export default function AmendmentModal({ type, record, field, fieldLabel, currentValue, onSubmit, onClose, users }) {
  const [newValue, setNewValue] = useState(currentValue != null ? String(currentValue) : "");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const amendments = record?.amendments || [];

  const getUserName = (id) => {
    const u = (users || []).find((usr) => usr.id === id);
    return u ? `${u.first_name} ${u.last_name}` : "Unknown";
  };

  const formatDateTime = (d) => {
    if (!d) return "—";
    const dt = new Date(d);
    return dt.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
  };

  const handleSubmit = async () => {
    if (!reason || reason.trim().length < 3) {
      setError("Please provide a reason for this correction (at least 3 characters).");
      return;
    }
    if (String(newValue).trim() === String(currentValue)) {
      setError("The new value is the same as the current value.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit({ field_changed: field, new_value: newValue, reason: reason.trim() });
      onClose();
    } catch (e) {
      setError(e.error || "Amendment failed. Please try again.");
      setSubmitting(false);
    }
  };

  // Determine if this field is numeric
  const isNumeric = ["motor_threshold_value", "target_intensity_pct", "final_intensity_pct",
    "delivered_total_pulses", "total_score"].includes(field);

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex",
      alignItems: "center", justifyContent: "center", zIndex: 1000,
    }} onClick={onClose}>
      <div style={{
        background: COLORS.surface, borderRadius: "16px", width: "480px", maxWidth: "95vw",
        maxHeight: "90vh", overflow: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
      }} onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div style={{ padding: "20px 24px 0", borderBottom: `1px solid ${COLORS.borderLight}`, paddingBottom: "16px" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <h3 style={{ fontSize: "16px", fontWeight: "700", color: COLORS.text, margin: 0 }}>
              Amend {type === "session" ? "Session" : "Assessment"} Record
            </h3>
            <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: COLORS.textSecondary, fontSize: "20px", lineHeight: 1, padding: "4px" }}>×</button>
          </div>
          <p style={{ fontSize: "12px", color: COLORS.textSecondary, marginTop: "4px" }}>
            All changes are logged with your identity, timestamp, the original value, and your reason.
          </p>
        </div>

        {/* Body */}
        <div style={{ padding: "20px 24px" }}>
          {/* Field being amended */}
          <div style={{ marginBottom: "16px" }}>
            <label style={{ fontSize: "12px", fontWeight: "600", color: COLORS.textSecondary, textTransform: "uppercase", letterSpacing: "0.3px" }}>
              Field
            </label>
            <div style={{ fontSize: "14px", fontWeight: "600", color: COLORS.text, marginTop: "4px" }}>{fieldLabel}</div>
          </div>

          {/* Current value */}
          <div style={{ marginBottom: "16px" }}>
            <label style={{ fontSize: "12px", fontWeight: "600", color: COLORS.textSecondary, textTransform: "uppercase", letterSpacing: "0.3px" }}>
              Current Value
            </label>
            <div style={{
              fontSize: "14px", color: COLORS.text, marginTop: "4px", padding: "8px 12px",
              background: COLORS.inputBg, borderRadius: "6px", border: `1px solid ${COLORS.borderLight}`,
            }}>
              {currentValue != null && currentValue !== "" ? String(currentValue) : "—"}
            </div>
          </div>

          {/* New value */}
          <div style={{ marginBottom: "16px" }}>
            <label style={{ fontSize: "12px", fontWeight: "600", color: COLORS.textSecondary, textTransform: "uppercase", letterSpacing: "0.3px" }}>
              Corrected Value
            </label>
            {field === "session_notes" || field === "notes" ? (
              <textarea
                value={newValue}
                onChange={(e) => setNewValue(e.target.value)}
                rows={3}
                style={{
                  width: "100%", marginTop: "4px", padding: "8px 12px", fontSize: "14px",
                  border: `1px solid ${COLORS.border}`, borderRadius: "8px", fontFamily: "inherit",
                  resize: "vertical", boxSizing: "border-box",
                }}
              />
            ) : (
              <input
                type={isNumeric ? "number" : "text"}
                value={newValue}
                onChange={(e) => setNewValue(e.target.value)}
                style={{
                  width: "100%", marginTop: "4px", padding: "8px 12px", fontSize: "14px",
                  border: `1px solid ${COLORS.border}`, borderRadius: "8px", boxSizing: "border-box",
                }}
              />
            )}
          </div>

          {/* Reason (required) */}
          <div style={{ marginBottom: "16px" }}>
            <label style={{ fontSize: "12px", fontWeight: "600", color: COLORS.textSecondary, textTransform: "uppercase", letterSpacing: "0.3px" }}>
              Reason for Correction <span style={{ color: COLORS.danger }}>*</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Patient reported score was 18, not 8 — data entry error"
              rows={2}
              style={{
                width: "100%", marginTop: "4px", padding: "8px 12px", fontSize: "13px",
                border: `1px solid ${COLORS.border}`, borderRadius: "8px", fontFamily: "inherit",
                resize: "vertical", boxSizing: "border-box",
              }}
            />
          </div>

          {error && (
            <div style={{ padding: "8px 12px", background: "#FDEDEC", borderRadius: "6px", fontSize: "12px", color: COLORS.danger, marginBottom: "16px" }}>
              {error}
            </div>
          )}

          {/* Previous amendments for this record */}
          {amendments.length > 0 && (
            <div style={{ marginTop: "8px", padding: "12px", background: "#FFFBE6", borderRadius: "8px", border: "1px solid #F0E68C" }}>
              <div style={{ fontSize: "11px", fontWeight: "700", color: "#8B7000", textTransform: "uppercase", letterSpacing: "0.3px", marginBottom: "8px" }}>
                Previous Amendments ({amendments.length})
              </div>
              {amendments.map((am) => (
                <div key={am.id} style={{ fontSize: "12px", color: COLORS.text, marginBottom: "6px", paddingBottom: "6px", borderBottom: `1px solid #F0E68C` }}>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span style={{ fontWeight: "600" }}>{am.field_changed}</span>
                    <span style={{ color: COLORS.textSecondary, fontSize: "10px" }}>{formatDateTime(am.amended_at)}</span>
                  </div>
                  <div style={{ fontSize: "11px", color: COLORS.textSecondary, marginTop: "2px" }}>
                    {am.old_value} → {am.new_value} — by {getUserName(am.amended_by)}
                  </div>
                  <div style={{ fontSize: "11px", fontStyle: "italic", color: "#8B7000", marginTop: "2px" }}>
                    "{am.reason}"
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: "16px 24px", borderTop: `1px solid ${COLORS.borderLight}`,
          display: "flex", justifyContent: "flex-end", gap: "8px",
        }}>
          <button onClick={onClose} style={{
            padding: "8px 20px", borderRadius: "8px", border: `1px solid ${COLORS.border}`,
            background: COLORS.surface, color: COLORS.textSecondary, fontSize: "13px", fontWeight: "600", cursor: "pointer",
          }}>Cancel</button>
          <button onClick={handleSubmit} disabled={submitting} style={{
            padding: "8px 20px", borderRadius: "8px", border: "none",
            background: submitting ? COLORS.border : "#D4700A", color: "#FFF",
            fontSize: "13px", fontWeight: "600", cursor: submitting ? "not-allowed" : "pointer",
          }}>{submitting ? "Saving..." : "Save Amendment"}</button>
        </div>
      </div>
    </div>
  );
}
